import { toast } from 'vue-sonner'
import { formatTimer } from '../../utils/callUtils.js'
import { useCallTimer } from './useCallTimer.js'
import { useCallState, resetCallState } from './useCallState.js'
import { useCallActions } from './useCallActions.js'
import { useCallFormatting } from './useCallFormatting.js'
import { launchExternalCall } from '../../utils/callLauncher.js'
import { CallResult, CallCommentaireSource } from '../../constants/commercial-call.js'
import { quotesService, callsService } from '../../services/index.js'
import { prendreDemandePourAppel, rendreDemandeApresAnnulation, oublierPriseDeLAppel } from './useCallAssignment.js'

/**
 * Composable global pour gérer un seul appel à la fois dans toute l'application
 * Limite: 100 lignes max
 */
const beforeUnloadHandler = (e) => {
  const callState = useCallState()
  if (callState.callInProgress.value) {
    e.preventDefault()
    e.returnValue = 'Un appel est en cours. Veuillez terminer l\'appel avant de quitter la page.'
    return e.returnValue
  }
}

export function useGlobalCall() {
  const callState = useCallState()
  const { formatCallNotes } = useCallFormatting()
  const { startTimer, stopTimer } = useCallTimer(
    callState.callStartTime,
    callState.callDuration
  )

  const {
    saveCall,
    sendAvailabilityRequest,
    handleGenerateAndSendQuote,
    proposePhoneAppointment,
    confirmNoAnswer
  } = useCallActions(callState, stopTimer, beforeUnloadHandler)

  const formatTimerFn = (seconds) => formatTimer(seconds)

  const startCall = (quoteId, quote, callbacks = {}, direction = 'sortant') => {
    if (callState.callInProgress.value) {
      toast.warning('Un appel est déjà en cours. Veuillez terminer l\'appel actuel avant d\'en démarrer un nouveau.')
      return false
    }

    callState.callInProgress.value = true
    callState.quoteId.value = quoteId
    callState.quote.value = quote
    callState.callStartTime.value = Date.now()
    callState.callDuration.value = 0
    callState.callComments.value = ''
    callState.callDirection.value = direction

    callState.callbacks.value = {
      updateStatus: callbacks.updateStatus || null,
      loadQuote: callbacks.loadQuote || null,
      loadHistory: callbacks.loadHistory || null,
      loadComments: callbacks.loadComments || null,
      generateAndSendQuote: callbacks.generateAndSendQuote || null
    }

    callState.callTimerInterval.value = startTimer()
    window.addEventListener('beforeunload', beforeUnloadHandler)

    // Lancement externe (Teams/tel) — no-op tant que non configuré (voir callLauncher.js).
    launchExternalCall(direction, quote?.telephone)

    // Appeler une demande libre la donne au commercial qui appelle.
    prendreDemandePourAppel(quoteId, () => {
      if (String(callState.quoteId.value) !== String(quoteId)) return
      callState.assignedByCall.value = true
      toast.info('La demande vous est assignée.')
      refreshQuoteView()
    })

    return true
  }

  // Enregistrement de l'appel à la validation du tunnel « Analyse du besoin ».
  //
  // L'appel N'EST PAS CLOS ICI : le commercial enchaîne sur l'édition puis
  // l'envoi du devis, et c'est l'envoi qui met fin à l'appel (closeCallAfterQuoteSent).
  // Auparavant le bandeau disparaissait dès la validation de l'analyse, ce qui
  // donnait l'impression que le parcours s'arrêtait là.
  //
  // L'appel est tout de même ENREGISTRÉ dès maintenant : si le commercial
  // abandonne avant d'envoyer le devis, la trace de l'échange existe quand même.
  //
  // `commentaireBesoin` est le point de vigilance saisi dans le tunnel. Il est
  // versé aux notes de l'appel pour remonter dans le fil de la fiche, sous sa
  // propre mention. Vide, il ne crée aucun commentaire (garde côté serveur).
  const endCallFromBesoin = async (commentaireBesoin = '') => {
    if (!callState.callInProgress.value) return
    const saisie = (commentaireBesoin || '').trim()
    if (saisie) {
      callState.callComments.value = saisie
    }
    try {
      await saveCall({
        result: CallResult.ANSWERED,
        sendEmail: true,
        keepCallActive: true,
        commentaireSource: saisie ? CallCommentaireSource.ANALYSE_BESOIN : CallCommentaireSource.APPEL
      })
    } catch (err) {
      // L'erreur est déjà gérée dans saveCall (toast).
    }
  }

  // Fin de l'appel une fois le devis envoyé au client : l'échange est terminé.
  //
  // `quoteIdEnvoye` est OBLIGATOIRE et vérifié : le bandeau est global et
  // survit à la navigation. Sans cette garde, envoyer le devis d'une demande
  // pendant qu'on est au téléphone sur une AUTRE effacerait l'appel en cours
  // sans l'enregistrer ni prévenir.
  //
  // La durée est refermée ici, pas à la validation de l'analyse : la
  // conversation a continué pendant l'édition du devis, et l'historique doit
  // porter le temps réel de l'échange, pas celui du milieu du parcours.
  const closeCallAfterQuoteSent = async (quoteIdEnvoye) => {
    if (!callState.callInProgress.value) return
    if (String(callState.quoteId.value) !== String(quoteIdEnvoye)) return

    const callId = callState.savedCallId.value
    const dureeFinale = callState.callDuration.value
    const notesFinales = formatCallNotes(callState.callComments.value, dureeFinale)

    if (callId) {
      try {
        await callsService.update(callId, { durationSeconds: dureeFinale, notes: notesFinales })
      } catch (err) {
        // L'échange est terminé quoi qu'il arrive : on ne bloque pas la clôture
        // sur un échec de mise à jour de la durée.
        console.error('Mise à jour de la durée d\'appel impossible', err)
      }
    }

    completeCall()
  }

  const markCallAsNoAnswer = () => {
    callState.sendEmailOnNoAnswer.value = true
    callState.showNoAnswerModal.value = true
  }

  const setNoAnswerDetails = ({ voicemail, emailVariant, customMessage }) => {
    callState.noAnswerVoicemail.value = voicemail || null
    callState.noAnswerEmailVariant.value = emailVariant || 'basic'
    callState.noAnswerCustomMessage.value = customMessage || ''
  }

  const abandonCall = () => {
    if (!callState.callInProgress.value) return
    // Une fois l'analyse du besoin validée, l'appel EST enregistré (ligne
    // d'historique, commentaire éventuel, assignation du commercial). Promettre
    // le contraire ferait croire à une annulation propre.
    const dejaEnregistre = Boolean(callState.savedCallId.value)
    // Sans issue enregistrée, la demande que l'appel avait prise redevient libre.
    const rendLaDemande = !dejaEnregistre && callState.assignedByCall.value
    let question = 'Êtes-vous sûr de vouloir annuler cette action ?\n\nAucune modification ne sera enregistrée dans le back-office.'
    if (dejaEnregistre) question = 'Terminer cet appel ?\n\nIl restera dans l\'historique de la demande, avec sa durée.'
    else if (rendLaDemande) question = 'Êtes-vous sûr de vouloir annuler cet appel ?\n\nLa demande redeviendra non assignée.'
    if (!confirm(question)) {
      return
    }
    const loadQuoteFn = callState.callbacks.value.loadQuote
    const liberation = dejaEnregistre ? null : rendreDemandeApresAnnulation()
    completeCall()
    if (dejaEnregistre) {
      toast.info('Appel terminé')
      return
    }
    liberation.then((rendue) => {
      toast.info(rendue ? 'Appel annulé — la demande redevient non assignée' : 'Appel annulé — aucune donnée enregistrée')
      // La fiche peut avoir été quittée entre-temps : rafraîchissement sans garantie.
      if (rendue && loadQuoteFn) {
        Promise.resolve().then(loadQuoteFn).catch((err) => console.error('Rafraîchissement de la demande impossible', err))
      }
    })
  }

  const markCallAsNotInterested = () => {
    callState.showNotInterestedModal.value = true
  }

  const closeNotInterestedModal = () => {
    callState.showNotInterestedModal.value = false
  }

  const markCallScheduleRelance = () => {
    callState.showRelanceModal.value = true
  }

  const closeRelanceModal = () => {
    callState.showRelanceModal.value = false
  }

  // Réinitialise l'état d'appel après une issue enregistrée côté serveur (sans re-sauvegarder).
  //
  // L'intervalle est coupé depuis l'ÉTAT PARTAGÉ et non via stopTimer() : ce
  // dernier ne connaît que la closure de l'instance qui a démarré le chrono.
  // Une clôture demandée depuis un autre composant (l'envoi du devis, par
  // exemple) laissait sinon tourner un intervalle orphelin par fiche visitée.
  const completeCall = () => {
    if (callState.callTimerInterval.value) clearInterval(callState.callTimerInterval.value)
    stopTimer()
    resetCallState()
    oublierPriseDeLAppel()
    window.removeEventListener('beforeunload', beforeUnloadHandler)
  }

  // Rafraîchit la page devis courante après une issue (best-effort : la page peut être démontée).
  const refreshQuoteView = async () => {
    const loadHistoryFn = callState.callbacks.value.loadHistory
    const loadQuoteFn = callState.callbacks.value.loadQuote
    try {
      if (loadHistoryFn) await loadHistoryFn()
      if (loadQuoteFn) await loadQuoteFn()
    } catch (err) {
      // La vue devis n'est peut-être plus montée : rafraîchissement ignoré.
    }
  }

  // Renseigne les détails « Ne répond pas » puis enregistre l'appel (email + répondeur).
  const submitNoAnswer = ({ emailVariant, customMessage, voicemailChoice, comment }) => {
    callState.sendEmailOnNoAnswer.value = true
    setNoAnswerDetails({ voicemail: voicemailChoice, emailVariant, customMessage })
    callState.callComments.value = comment ? comment.trim() : ''
    confirmNoAnswer()
  }

  // Enregistre l'issue « Pas intéressé » sur le devis de l'appel courant, puis clôt l'appel.
  const confirmNotInterested = async ({ lossReasonId, comment, doNotContact }) => {
    if (callState.savingNotInterested.value) {
      return
    }
    const quoteIdVal = callState.quoteId.value
    const direction = callState.callDirection.value
    callState.savingNotInterested.value = true
    try {
      await quotesService.markNotInterested(quoteIdVal, { lossReasonId, comment, doNotContact, direction })
      closeNotInterestedModal()
      completeCall()
      toast.success('Demande enregistrée comme « Pas intéressé »')
      await refreshQuoteView()
    } catch (err) {
      toast.error(err.message || 'Erreur lors de l\'enregistrement')
      console.error(err)
    } finally {
      callState.savingNotInterested.value = false
    }
  }

  // Programme une relance sur le devis de l'appel courant, puis clôt l'appel.
  const confirmRelance = async ({ motif, dueDate, priorite, notifyClient }) => {
    if (callState.savingRelance.value) {
      return
    }
    const quoteIdVal = callState.quoteId.value
    const direction = callState.callDirection.value
    callState.savingRelance.value = true
    try {
      await quotesService.scheduleRelance(quoteIdVal, { motif, dueDate, priorite, direction, notifyClient })
      closeRelanceModal()
      completeCall()
      toast.success('Relance programmée')
      await refreshQuoteView()
    } catch (err) {
      toast.error(err.message || 'Erreur lors de la programmation de la relance')
      console.error(err)
    } finally {
      callState.savingRelance.value = false
    }
  }

  const openValidateInfoModal = (quoteIdForEdit = null) => {
    // « Analyse du besoin » : popup quasi plein écran par-dessus la page courante
    // (plus de navigation vers un tunnel dédié). Le contenu est identique.
    const targetId = quoteIdForEdit || callState.quoteId.value
    if (!targetId) return
    callState.besoinQuoteId.value = targetId
    callState.besoinModalOpen.value = true
  }

  const closeBesoinModal = () => {
    callState.besoinModalOpen.value = false
  }

  const cleanup = () => {
    stopTimer()
    callState.callTimerInterval.value = null
    window.removeEventListener('beforeunload', beforeUnloadHandler)
  }

  return {
    callInProgress: callState.callInProgress,
    quoteId: callState.quoteId,
    quote: callState.quote,
    callDuration: callState.callDuration,
    callComments: callState.callComments,
    callDirection: callState.callDirection,
    sendingEmail: callState.sendingEmail,
    generatingQuote: callState.generatingQuote,
    savingCall: callState.savingCall,
    savingNotInterested: callState.savingNotInterested,
    savingRelance: callState.savingRelance,
    showNoAnswerModal: callState.showNoAnswerModal,
    showNotInterestedModal: callState.showNotInterestedModal,
    showRelanceModal: callState.showRelanceModal,
    besoinModalOpen: callState.besoinModalOpen,
    besoinQuoteId: callState.besoinQuoteId,
    sendEmailOnNoAnswer: callState.sendEmailOnNoAnswer,
    formatTimer: formatTimerFn,
    startCall,
    endCallFromBesoin,
    closeCallAfterQuoteSent,
    abandonCall,
    sendAvailabilityRequest,
    proposePhoneAppointment,
    handleGenerateAndSendQuote,
    markCallAsNoAnswer,
    setNoAnswerDetails,
    submitNoAnswer,
    markCallAsNotInterested,
    closeNotInterestedModal,
    confirmNotInterested,
    markCallScheduleRelance,
    closeRelanceModal,
    confirmRelance,
    completeCall,
    confirmNoAnswer,
    openValidateInfoModal,
    closeBesoinModal,
    cleanup,
    updateCallComments: (value) => { callState.callComments.value = value },
    updateCallDirection: (value) => { callState.callDirection.value = value }
  }
}

